// frontend/src/features/ups/modals/SiteDrawer.js
// UPS Admin: Site Drawer
// - Read-only code (immutable)
// - Rename site (name only)
// - Deactivate / Reactivate via PATCH /ups-admin/sites/:id { status: 'inactive'|'active' }
// - Agencies tab is control-plane only: contract existence + site configuration

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import {
  createContractApi,
  fetchSiteAgencyPermissionsApi,
  setContractSiteStatusApi,
} from '../../../services/contractService';

// ==========================================
// SECTION 1: SMALL UI HELPERS
// ==========================================
function normalizeStatus(s) {
  const v = (s ?? '').toString().trim().toLowerCase();
  if (v === 'inactive') return 'inactive';
  return 'active';
}

function StatusBadge({ status }) {
  const st = normalizeStatus(status);
  const cls =
    st === 'active'
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-50 text-gray-700 border-gray-200';

  return (
    <span className={`inline-flex items-center rounded-lg border px-2 py-1 text-xs font-semibold ${cls}`}>
      {st}
    </span>
  );
}

function ContractBadge({ contractStatus }) {
  const normalized = (contractStatus ?? '').toString().trim().toLowerCase();

  if (normalized === 'active') {
    return (
      <span className="inline-flex items-center rounded-lg border border-green-200 bg-green-50 px-2 py-1 text-xs font-semibold text-green-700">
        Contract active
      </span>
    );
  }

  if (normalized === 'disabled') {
    return (
      <span className="inline-flex items-center rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
        Contract disabled
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-700">
      No contract
    </span>
  );
}

// ==========================================
// SECTION 2: DRAWER STATE
// Local drawer state only. Site-agency permissions are not stored in Redux.
// ==========================================
export default function SiteDrawer({ isOpen, site, onClose, onChanged }) {
  const [tab, setTab] = useState('details'); // details | agencies

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [error, setError] = useState('');

  // Local copy to reflect status changes immediately without relying on parent refresh timing.
  const [localSite, setLocalSite] = useState(null);

  // Agencies tab state stays local to this drawer.
  const [agencyRows, setAgencyRows] = useState([]);
  const [agenciesLoading, setAgenciesLoading] = useState(false);
  const [agenciesError, setAgenciesError] = useState('');
  const [agenciesWarning, setAgenciesWarning] = useState('');
  const [rowSaving, setRowSaving] = useState({});
  const latestAgenciesRequestRef = useRef(0);

  const status = useMemo(() => normalizeStatus(localSite?.status), [localSite]);
  const currentSiteId = localSite?.id ?? site?.id ?? null;

  // ==========================================
  // SECTION 3: AGENCIES TAB DATA LOADING
  // Loads backend truth and protects against stale responses when switching sites.
  // ==========================================
  // Invalidates any in-flight agencies request and clears drawer-only agency state.
  function clearAgenciesState() {
    latestAgenciesRequestRef.current += 1;
    setAgencyRows([]);
    setAgenciesLoading(false);
    setAgenciesError('');
    setAgenciesWarning('');
    setRowSaving({});
  }

  // Tracks row-level saving so one agency action does not lock the whole drawer.
  function setRowSavingState(agencyId, isSaving) {
    const key = String(agencyId);
    setRowSaving((prev) => {
      if (isSaving) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  // Loads the authoritative site-agency permission matrix from the backend.
  // The request id guard prevents stale responses when switching sites quickly.
  async function loadSiteAgencies(siteId) {
    if (!siteId) return false;

    
    const requestId = latestAgenciesRequestRef.current + 1;
    latestAgenciesRequestRef.current = requestId;

    setAgenciesLoading(true);
    setAgenciesError('');
    setAgenciesWarning('');

    try {
      const data = await fetchSiteAgencyPermissionsApi(siteId);
      if (latestAgenciesRequestRef.current !== requestId) return false;

      setAgencyRows(Array.isArray(data?.agencies) ? data.agencies : []);
      if (data?.site && typeof data.site === 'object') {
        setLocalSite((prev) => ({
          ...(prev || {}),
          ...data.site,
        }));
      }
      setAgenciesWarning('');
      return true;
    } catch (e) {
      if (latestAgenciesRequestRef.current !== requestId) return false;

      setAgencyRows([]);
      setAgenciesError(e?.response?.data?.message || 'Failed to load site agencies');
      return false;
    } finally {
      if (latestAgenciesRequestRef.current === requestId) {
        setAgenciesLoading(false);
        setRowSaving({});
      }
    }
  }

  // ==========================================
  // SECTION 4: DRAWER LIFECYCLE EFFECTS
  // Keeps drawer state in sync with open/close, selected site, and active tab.
  // ==========================================
  // Reset drawer details whenever a new site is opened.
  useEffect(() => {
    if (!isOpen || !site) return;

    setTab('details');
    setError('');
    setSaving(false);
    setStatusBusy(false);

    setLocalSite(site);
    setName(site?.name || '');
  }, [isOpen, site]);

  // Clear agencies tab state immediately when the drawer closes or site changes.
  useEffect(() => {
    if (!isOpen) {
      clearAgenciesState();
      return;
    }

    clearAgenciesState();
  }, [isOpen, currentSiteId]);

  // Only fetch agencies when the Agencies tab is visible.
  useEffect(() => {
    if (!isOpen) return;
    if (tab !== 'agencies') return;
    if (!currentSiteId) return;

    loadSiteAgencies(currentSiteId);
  }, [isOpen, tab, currentSiteId]);

  // ==========================================
  // SECTION 5: SITE DETAILS ACTIONS
  // Rename and activate/deactivate the selected site.
  // ==========================================
    const saveName = async () => {
    if (!localSite?.id) return;
    setError('');

    const n = (name ?? '').trim();
    if (!n) return setError('Site name is required');

    setSaving(true);
    try {
      const res = await axios.patch(`/ups-admin/sites/${localSite.id}`, { name: n });

      const updated = res.data?.site ?? res.data ?? null;
      setLocalSite((prev) => ({
        ...(prev || {}),
        ...(updated && typeof updated === 'object' ? updated : {}),
        name: n,
      }));

      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update site');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (nextStatus) => {
    if (!localSite?.id) return;
    const next = normalizeStatus(nextStatus);
    setError('');

    setStatusBusy(true);
    try {
      const res = await axios.patch(`/ups-admin/sites/${localSite.id}`, { status: next });

      const updated = res.data?.site ?? res.data ?? null;
      setLocalSite((prev) => ({
        ...(prev || {}),
        ...(updated && typeof updated === 'object' ? updated : {}),
        status: next,
      }));

      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  };

  // ==========================================
  // SECTION 6: AGENCY CONTRACT/SITE ACCESS ACTIONS
  // Creates company-agency contracts and toggles per-site access.
  // Mutations are followed by a refetch so the UI renders backend truth.
  // ==========================================
  const refreshAgencies = async () => {
    if (!currentSiteId) return false;
    return loadSiteAgencies(currentSiteId);
  };

  // Create only the company-agency contract. Site access is enabled separately.
  const handleCreateContract = async (agencyId) => {
    if (!agencyId) return;

    setRowSavingState(agencyId, true);
    setAgenciesError('');
    setAgenciesWarning('');

    try {
      await createContractApi({ agencyId });

      const refreshed = await refreshAgencies();

      if (!refreshed) {
        setAgenciesWarning(
          'Contract was created, but the drawer could not refresh. Retry refresh to load the latest contract and site access state.'
        );
      }
    } catch (e) {
      setAgenciesError(e?.response?.data?.error || 'Failed to create contract');
    } finally {
      setRowSavingState(agencyId, false);
    }
  };

    // Toggle the contract-site permission, then refetch backend truth.
    const handleToggleSitePermission = async (row) => {
      const agencyId = row?.agency_id;
      const contractId = row?.contract_id;
      if (!agencyId || !contractId || !currentSiteId) return;

      const nextStatus = row.configured_allowed_at_site ? 'disabled' : 'active';

      setRowSavingState(agencyId, true);
      setAgenciesError('');
      setAgenciesWarning('');

      try {
        await setContractSiteStatusApi(contractId, currentSiteId, nextStatus);

        const refreshed = await refreshAgencies();

        if (!refreshed) {
          setAgenciesWarning(
            'Site access was updated, but the drawer could not refresh. Retry refresh to confirm the latest site access state.'
          );
        }
      } catch (e) {
        setAgenciesError(e?.response?.data?.error || 'Failed to update site access');
      } finally {
        setRowSavingState(agencyId, false);
      }
    };


  // ==========================================
  // SECTION 7: RENDER GUARD
  // Do not render the drawer unless it is open and has a selected site.
  // ==========================================  
    if (!isOpen || !site) return null;

  // ==========================================
  // SECTION 8: DRAWER UI
  // Header, tabs, Details tab, and Agencies tab.
  // ==========================================  
  return (
    <div className="absolute inset-x-0 bottom-0 top-[var(--ups-shell-header-h)] z-40">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      <aside className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-app-border bg-app-card text-app-text shadow-xl sm:w-[520px] md:w-[640px]">
        <div className="sticky top-0 border-b border-app-border bg-app-card p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-app-muted">Site</div>
              <div className="flex items-center gap-2">
                <div className="text-xl font-semibold text-app-text">{localSite?.name || site.name}</div>
                <StatusBadge status={localSite?.status} />
              </div>
              <div className="text-sm text-app-muted">
                Code: <span className="font-mono text-xs">{localSite?.code || site.code || '-'}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 text-app-muted transition hover:bg-app-surface hover:text-app-text"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                tab === 'details'
                  ? 'border-app-primary bg-app-primary text-white shadow-sm'
                  : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
              }`}
              onClick={() => setTab('details')}
            >
              Details
            </button>
            <button
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                tab === 'agencies'
                  ? 'border-app-primary bg-app-primary text-white shadow-sm'
                  : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
              }`}
              onClick={() => setTab('agencies')}
            >
              Agencies
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          {tab === 'details' && (
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-sm text-app-muted">Site name</label>
                <input
                  className="w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={saving || statusBusy}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-app-muted">Code (immutable)</label>
                <input
                  className="w-full rounded-xl border border-app-border bg-app-surface px-4 py-3 font-mono text-sm text-app-muted outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
                  value={(localSite?.code || site.code || '').toString()}
                  readOnly
                />
                <div className="mt-1 text-xs text-app-muted">
                  Code cannot be changed after creation.
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-app-muted">
                  Status: <StatusBadge status={localSite?.status} />
                </div>

                <div className="flex items-center gap-2">
                  {status === 'active' ? (
                    <button
                      onClick={() => setStatus('inactive')}
                      disabled={statusBusy || saving}
                      className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:opacity-60"
                    >
                      {statusBusy ? 'Updating...' : 'Deactivate'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus('active')}
                      disabled={statusBusy || saving}
                      className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:opacity-60"
                    >
                      {statusBusy ? 'Updating...' : 'Reactivate'}
                    </button>
                  )}

                  <button
                    onClick={saveName}
                    disabled={saving || statusBusy}
                    className="rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="text-xs text-app-muted">
                Created:{' '}
                {localSite?.created_at || site.created_at
                  ? new Date(localSite?.created_at || site.created_at).toLocaleString()
                  : '-'}
              </div>
            </div>
          )}

          {tab === 'agencies' && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-app-text">Linked agencies</div>
                  <div className="text-sm text-app-muted">
                    Control-plane view of contract existence, site configuration, and operationally
                    allowed state for this site.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => refreshAgencies()}
                  disabled={agenciesLoading || !currentSiteId}
                  className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:opacity-60"
                >
                  {agenciesLoading ? 'Refreshing...' : 'Retry refresh'}
                </button>
              </div>

              {agenciesWarning && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <div>{agenciesWarning}</div>
                  <button
                    type="button"
                    onClick={() => refreshAgencies()}
                    disabled={agenciesLoading || !currentSiteId}
                    className="mt-3 rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:opacity-60"
                  >
                    Retry refresh
                  </button>
                </div>
              )}

              {agenciesError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <div>{agenciesError}</div>
                  <button
                    type="button"
                    onClick={() => refreshAgencies()}
                    disabled={agenciesLoading || !currentSiteId}
                    className="mt-3 rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:opacity-60"
                  >
                    Retry refresh
                  </button>
                </div>
              )}

              {agenciesLoading && agencyRows.length === 0 && (
                <div className="rounded-xl border border-app-border bg-app-surface px-4 py-8 text-sm text-app-muted">
                  Loading agencies for this site...
                </div>
              )}

              {!agenciesLoading && !agenciesError && agencyRows.length === 0 && (
                <div className="rounded-xl border border-app-border bg-app-surface px-4 py-8 text-sm text-app-muted">
                  No agencies found for this company.
                </div>
              )}

              {agencyRows.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-app-border bg-app-card">
                  <div className="grid grid-cols-[minmax(0,1.4fr)_140px_170px_160px] gap-3 border-b border-app-border bg-app-surface px-4 py-3 text-xs font-semibold uppercase tracking-wide text-app-muted">
                    <div>Agency</div>
                    <div>Contract</div>
                    <div>Allowed at this site</div>
                    <div>Action</div>
                  </div>

                  <div className="divide-y divide-app-border">
                    {agencyRows.map((row) => {
                      const agencyId = row.agency_id;
                      const isSavingRow = !!rowSaving[String(agencyId)];
                      const hasAuthoritativeWarning = !!agenciesWarning;
                      const canToggle =
                        !!row.contract_exists &&
                        !isSavingRow &&
                        !agenciesLoading &&
                        !hasAuthoritativeWarning;
                      const configuredChecked = !!row.configured_allowed_at_site;

                      return (
                        <div
                          key={agencyId}
                          className="grid grid-cols-[minmax(0,1.4fr)_140px_170px_160px] gap-3 px-4 py-4 items-start"
                        >
                          <div className="min-w-0">
                            <div className="truncate font-medium text-app-text">{row.agency_name}</div>
                            <div className="mt-1 text-xs text-app-muted">
                              Contract exists: {row.contract_exists ? 'Yes' : 'No'}
                            </div>
                            <div className="mt-1 text-xs text-app-muted">
                              Operationally allowed: {row.operationally_allowed_at_site ? 'Yes' : 'No'}
                            </div>
                            {row.contract_exists && row.contract_status === 'disabled' && (
                              <div className="mt-1 text-xs text-amber-700">
                                Contract disabled. Operationally allowed stays off until the contract is active.
                              </div>
                            )}
                          </div>

                          <div>
                            <ContractBadge contractStatus={row.contract_status} />
                          </div>

                          <div>
                            <label className="inline-flex items-center gap-2 text-sm text-app-text">
                              <input
                                type="checkbox"
                                checked={configuredChecked}
                                onChange={() => handleToggleSitePermission(row)}
                                disabled={!canToggle}
                                className="h-4 w-4"
                              />
                              <span>{configuredChecked ? 'On' : 'Off'}</span>
                            </label>
                            {!row.contract_exists && (
                              <div className="mt-1 text-xs text-app-muted">
                                Create contract to configure this site.
                              </div>
                            )}
                            {row.contract_exists && (
                              <div className="mt-1 text-xs text-app-muted">
                                Site permission: {row.site_permission_status}
                              </div>
                            )}
                          </div>

                          <div>
                            {!row.contract_exists ? (
                              <button
                                type="button"
                                onClick={() => handleCreateContract(agencyId)}
                                disabled={isSavingRow || agenciesLoading || hasAuthoritativeWarning}
                                className="rounded-xl bg-app-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition disabled:opacity-60"
                              >
                                {isSavingRow ? 'Creating...' : 'Create contract'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleSitePermission(row)}
                                disabled={!canToggle}
                                className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:opacity-60"
                              >
                                {isSavingRow ? 'Saving...' : configuredChecked ? 'Disable' : 'Enable'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>      
    </aside>
    </div>
  );
}
